// useNextStep — single source of truth for "where should this user go next
// in the funnel?". Used by both the bottom-of-page NextStepRail and the
// top-nav NextChip so the two surfaces always agree.
//
// Contract: returns one CTA object based on (current route, auth state).
// The rule is "always nudge one stage forward in the Path to AI Mastery":
//
//   Spark (free)  →  Stack (signup gate)  →  Sharpen  →  Operate  →  Mastery
//
// Guests on the Spark path see a signup CTA (label = openAuth flag).
// Signed-in users always see the next un-finished stage. Routes outside
// the path (Directory, Hack, Learn, etc.) get a context-appropriate
// "get on the path" pull-back.
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export interface NextStep {
  /** Stable id for analytics/instrumentation. */
  id: string;
  /** Short eyebrow shown above the label, e.g. "Stage 2 · Stack". */
  eyebrow: string;
  /** Primary CTA label (action verb first). */
  label: string;
  /** One-line preview shown on the rail variant. */
  blurb: string;
  /** Where to go. If `openAuth` is true, this is ignored and the auth
   *  modal opens instead. */
  href: string;
  /** When true, the rail/chip should open the AuthModal in signup mode
   *  rather than navigating. Used at the Spark → Stack signup gate. */
  openAuth?: boolean;
}

/** Path-stage routes — keep aligned with OnePathJourney STAGES. */
const STAGE_ROUTES = {
  spark: "/quests/paths/beginner-hacker",
  stack: "/quests/paths/power-21",
  sharpen: "/quests/paths/prompt-engineer",
  operate: "/quests/paths/ai-operator",
  mastery: "/roadmap",
} as const;

const NEXT_AFTER: Record<string, NextStep> = {
  // After Spark → push Stack. Guests get the signup gate; signed-in users
  // walk straight in.
  spark: {
    id: "next-stack",
    eyebrow: "Stage 2 · Stack",
    label: "Start The Power 21",
    blurb: "21 daily reps that turn AI from a toy into a habit.",
    href: STAGE_ROUTES.stack,
  },
  stack: {
    id: "next-sharpen",
    eyebrow: "Stage 3 · Sharpen",
    label: "Begin Prompt Engineer",
    blurb: "Precision prompting — make AI do exactly what you mean.",
    href: STAGE_ROUTES.sharpen,
  },
  sharpen: {
    id: "next-operate",
    eyebrow: "Stage 4 · Operate",
    label: "Become an AI Operator",
    blurb: "Chain tools, verify sources, ship real work.",
    href: STAGE_ROUTES.operate,
  },
  operate: {
    id: "next-mastery",
    eyebrow: "Final · Mastery",
    label: "Claim your certificate",
    blurb: "You've cleared every stage — make it official.",
    href: STAGE_ROUTES.mastery,
  },
};

const SPARK_GUEST_GATE: NextStep = {
  id: "next-stack-gated",
  eyebrow: "Stage 2 · Stack",
  label: "Sign up to unlock The Power 21",
  blurb: "Free account · keep your progress · unlock Stages 2–4.",
  href: STAGE_ROUTES.stack,
  openAuth: true,
};

const GUEST_DEFAULT: NextStep = {
  id: "next-spark-guest",
  eyebrow: "Stage 1 · Spark",
  label: "Try Lesson 1 free",
  blurb: "60-second first AI win. No signup needed.",
  href: STAGE_ROUTES.spark,
};

const AUTH_DEFAULT: NextStep = {
  id: "next-continue",
  eyebrow: "Your journey",
  label: "Continue your journey",
  blurb: "Pick up where you left off across the Path to AI Mastery.",
  href: "/quests",
};

/** Map a path-route to its stage key. */
const stageForRoute = (pathname: string): keyof typeof STAGE_ROUTES | null => {
  if (pathname.startsWith(STAGE_ROUTES.spark)) return "spark";
  if (pathname.startsWith(STAGE_ROUTES.stack)) return "stack";
  if (pathname.startsWith(STAGE_ROUTES.sharpen)) return "sharpen";
  if (pathname.startsWith(STAGE_ROUTES.operate)) return "operate";
  return null;
};

export const useNextStep = (): NextStep => {
  const { user } = useAuth();
  const { pathname } = useLocation();

  return useMemo<NextStep>(() => {
    const stage = stageForRoute(pathname);

    // On a stage page → push to the next stage.
    if (stage) {
      // Spark + guest = the canonical signup gate.
      if (stage === "spark" && !user) return SPARK_GUEST_GATE;
      const next = NEXT_AFTER[stage];
      if (next) return next;
    }

    // Off-path pages: pull guests onto Spark, signed-in users into /build.
    return user ? AUTH_DEFAULT : GUEST_DEFAULT;
  }, [pathname, user]);
};

export { STAGE_ROUTES };